import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, CurrencyCircleDollar, FileText, Clock, DownloadSimple, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AccountSettingsModal({ user, credits, onClose, onCreditsRequested }) {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [requestingInvoice, setRequestingInvoice] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [requestingCredits, setRequestingCredits] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/my-history`, { withCredentials: true });
      setHistory(data.logs || []);
    } catch {
      // Not critical
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRequestInvoice = async () => {
    setRequestingInvoice(true);
    try {
      await axios.post(`${API}/api/auth/invoice-request`, {}, { withCredentials: true });
      toast.success('Invoice request sent to administrator');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request invoice');
    } finally {
      setRequestingInvoice(false);
    }
  };

  const handleDownloadInvoice = async () => {
    setGeneratingPdf(true);
    try {
      const response = await axios.get(`${API}/api/auth/generate-invoice`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'NKOSIS_Invoice.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch {
      toast.error('Failed to generate invoice');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleRequestCredits = async () => {
    setRequestingCredits(true);
    try {
      await axios.post(`${API}/api/auth/request-credits`, {}, { withCredentials: true });
      toast.success('Credit request sent to admin!');
      if (onCreditsRequested) onCreditsRequested();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request credits');
    } finally {
      setRequestingCredits(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="account-settings-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-bold text-slate-800">Account Settings</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="close-account-modal">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* User Info */}
          <div className="flex items-start justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-800">{user?.name || 'User'}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="modal-credit-balance">
              <CurrencyCircleDollar size={16} weight="fill" className="text-amber-600" />
              <span className="text-sm font-bold text-amber-700">{credits ?? 0}</span>
              <span className="text-xs text-amber-600">credits</span>
            </div>
          </div>

          {/* Buy Credits */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <CurrencyCircleDollar size={18} className="text-amber-600" />
              <span className="text-sm font-semibold text-slate-800">Buy Credits</span>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Credits are required to run valuations and access map layers. Each valuation costs 1 credit (R250 excl. VAT).
            </p>
            <button
              onClick={handleRequestCredits}
              disabled={requestingCredits}
              className="w-full py-2 bg-slate-800 text-white text-xs font-semibold rounded-md hover:bg-slate-700 disabled:opacity-50 transition-colors"
              data-testid="buy-credits-btn"
            >
              {requestingCredits ? 'Sending...' : 'Request Credits from Admin'}
            </button>
            <p className="text-[10px] text-slate-400 mt-2 text-center">Payment gateway coming soon. For now, credit requests are processed by admin.</p>
          </div>

          {/* Invoice Section */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={18} className="text-blue-600" />
              <span className="text-sm font-semibold text-slate-800">Invoices</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDownloadInvoice}
                disabled={generatingPdf}
                className="flex-1 py-2 bg-white border border-slate-300 text-xs font-semibold text-slate-700 rounded-md hover:bg-slate-100 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-1.5"
                data-testid="download-invoice-btn"
              >
                <DownloadSimple size={14} />
                {generatingPdf ? 'Generating...' : 'Download Invoice PDF'}
              </button>
              <button
                onClick={handleRequestInvoice}
                disabled={requestingInvoice}
                className="flex-1 py-2 bg-white border border-slate-300 text-xs font-semibold text-slate-700 rounded-md hover:bg-slate-100 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-1.5"
                data-testid="request-invoice-btn"
              >
                <PaperPlaneTilt size={14} />
                {requestingInvoice ? 'Sending...' : 'Request from Admin'}
              </button>
            </div>
          </div>

          {/* Transaction History */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock size={18} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-800">Recent Transactions</span>
              <span className="text-xs text-slate-400 ml-auto">{history.length} valuations</span>
            </div>
            {loadingHistory ? (
              <div className="text-xs text-slate-400 py-4 text-center">Loading history...</div>
            ) : history.length === 0 ? (
              <div className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">
                No valuations yet
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-slate-500 font-semibold">Date</th>
                      <th className="px-3 py-2 text-left text-slate-500 font-semibold">Address</th>
                      <th className="px-3 py-2 text-right text-slate-500 font-semibold">Rent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {history.map((log, idx) => (
                      <tr key={log._id || idx} className="hover:bg-slate-50" data-testid={`history-row-${idx}`}>
                        <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                          {log.created_at ? new Date(log.created_at).toLocaleDateString() : '\u2014'}
                        </td>
                        <td className="px-3 py-2 text-slate-700 truncate max-w-[200px]">
                          {log.address || 'N/A'}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-slate-800">
                          {log.rent ? `R ${Number(log.rent).toLocaleString()}` : '\u2014'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 text-center">
          <p className="text-[10px] text-slate-400">
            Credits expire 2 months from allocation. See Terms of Service for details.
          </p>
        </div>
      </div>
    </div>
  );
}
